import { ServiceId } from "@mirage/discovery/id";
import * as services from "@mirage/discovery/services";
import * as rx from "rxjs";

import type { CachingStrategy } from "@mirage/service-search/service";
import type { Service } from "@mirage/service-typeahead-search/service";
import type { PreviousQuery } from "@mirage/shared/search/cache-result";
import type { RecentSectionTrack } from "@mirage/shared/search/section-tracking-record";
import type { URLShortcut } from "@mirage/shared/search/url-shortcut";
import type { CacheKey } from "./service/typeahead-cache";
import type { typeahead } from "./service/types";

export type { Service };

const service = services.get<Service>(ServiceId.TYPEAHEAD_SEARCH);

export const search = service.search;

export function cacheRecentQueries(queries: string[]) {
  return rx.firstValueFrom(service.cacheRecentQueries(queries));
}

export function getRecentQueries() {
  return rx.firstValueFrom(service.getRecentQueries());
}

export function getRecommendations() {
  return rx.firstValueFrom(service.getRecommendations());
}

export function getChannelRecommendations() {
  return rx.firstValueFrom(service.getChannelRecommendations());
}

export function getPeopleRecommendations() {
  return rx.firstValueFrom(service.getPeopleRecommendations());
}

export async function removePreviousQueryFromCache(
  result: typeahead.ScoredPreviousQuery,
) {
  return rx.firstValueFrom(service.remove(result as unknown as PreviousQuery));
}

export function clear(key: CacheKey) {
  return rx.firstValueFrom(service.clear(key));
}

export function tearDown() {
  return rx.firstValueFrom(service.tearDown());
}

export function startSyncRecommendations() {
  return rx.firstValueFrom(service.startSyncRecommendations());
}

export function cancelSyncRecommendations() {
  return rx.firstValueFrom(service.cancelSyncRecommendations());
}

export function startSyncPeopleRecommendations() {
  return rx.firstValueFrom(service.startSyncPeopleRecommendations());
}

export function cancelSyncPeopleRecommendations() {
  return rx.firstValueFrom(service.cancelSyncPeopleRecommendations());
}

export function startSyncChannelRecommendations() {
  return rx.firstValueFrom(service.startSyncChannelRecommendations());
}
export function cancelSyncChannelRecommendations() {
  return rx.firstValueFrom(service.cancelSyncChannelRecommendations());
}

export function startSyncOmniboxRecommendations() {
  return rx.firstValueFrom(service.startSyncOmniboxRecommendations());
}

export function cancelSyncOmniboxRecommendations() {
  return rx.firstValueFrom(service.cancelSyncOmniboxRecommendations());
}

export function startSyncWeights() {
  return rx.firstValueFrom(service.startSyncWeights());
}

export function cancelSyncWeights() {
  return rx.firstValueFrom(service.cancelSyncWeights());
}

export function startSyncSuggestedQueryScore() {
  return rx.firstValueFrom(service.startSyncSuggestedQueryScore());
}

export function cancelSyncSuggestedQueryScore() {
  return rx.firstValueFrom(service.cancelSyncSuggestedQueryScore());
}

export function registerHit(uuid: string) {
  return rx.firstValueFrom(service.registerHit(uuid));
}

export function trackSectionInteraction(
  sectionId: typeahead.SectionId | undefined,
  trackPayload: RecentSectionTrack,
) {
  return rx.firstValueFrom(
    service.trackSectionInteraction(sectionId, trackPayload),
  );
}

export const searchServiceCachingStrategy: CachingStrategy = {
  cacheQuery: (query) => {
    cacheRecentQueries([query]);
  },
};

export function remove(previousQuery: PreviousQuery) {
  return rx.firstValueFrom(service.remove(previousQuery));
}

export function addURLShortcutToCache(shortcut: URLShortcut) {
  return rx.firstValueFrom(service.addURLShortcutToCache(shortcut));
}
